import { useState } from 'react'
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  onSnapshot,
  QueryConstraint,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '../lib/firebase'

export interface FirestoreDocument {
  id: string
  [key: string]: any
}

export function useFirestore<T extends FirestoreDocument>(collectionName: string) {
  const [documents, setDocuments] = useState<T[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Obtener todos los documentos
  const getDocuments = async (constraints?: QueryConstraint[]) => {
    try {
      setLoading(true)
      setError(null)
      
      const collectionRef = collection(db, collectionName)
      const q = constraints ? query(collectionRef, ...constraints) : collectionRef
      const snapshot = await getDocs(q)
      
      const docs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as T[]
      
      setDocuments(docs)
      return docs
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al obtener documentos'
      setError(errorMessage)
      console.error('Error getting documents:', err)
      return []
    } finally {
      setLoading(false)
    }
  }

  // Obtener un documento por ID
  const getDocument = async (id: string): Promise<T | null> => {
    try {
      const docRef = doc(db, collectionName, id)
      const snapshot = await getDoc(docRef)
      
      if (snapshot.exists()) {
        return {
          id: snapshot.id,
          ...snapshot.data()
        } as T
      }
      return null
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al obtener documento'
      setError(errorMessage)
      console.error('Error getting document:', err)
      return null
    }
  }

  // Agregar un nuevo documento
  const addDocument = async (data: Omit<T, 'id'>): Promise<string | null> => {
    try {
      setError(null)
      
      console.log('🔥 [Firestore] Intentando agregar documento a colección:', collectionName)
      console.log('🔥 [Firestore] Datos a guardar:', data)
      
      const docData = {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
      
      console.log('🔥 [Firestore] Datos finales con timestamps:', docData)
      
      const collectionRef = collection(db, collectionName)
      console.log('🔥 [Firestore] Referencia de colección obtenida')
      
      const docRef = await addDoc(collectionRef, docData)
      console.log('🔥 [Firestore] ✅ Documento agregado exitosamente con ID:', docRef.id)
      
      // Actualizar la lista local
      await getDocuments()
      
      return docRef.id
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al agregar documento'
      console.error('🔥 [Firestore] ❌ Error completo:', err)
      console.error('🔥 [Firestore] ❌ Error message:', errorMessage)
      setError(errorMessage)
      return null
    }
  }

  // Actualizar un documento
  const updateDocument = async (id: string, data: Partial<Omit<T, 'id'>>): Promise<boolean> => {
    try {
      setError(null)
      
      const docData = {
        ...data,
        updatedAt: serverTimestamp()
      }
      
      const docRef = doc(db, collectionName, id)
      await updateDoc(docRef, docData)
      
      // Actualizar la lista local
      await getDocuments()
      
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al actualizar documento'
      setError(errorMessage)
      console.error('Error updating document:', err)
      return false
    }
  }

  // Eliminar un documento
  const deleteDocument = async (id: string): Promise<boolean> => {
    try {
      setError(null)
      
      const docRef = doc(db, collectionName, id)
      await deleteDoc(docRef)
      
      // Actualizar la lista local
      setDocuments(prev => prev.filter(doc => doc.id !== id))
      
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al eliminar documento'
      setError(errorMessage)
      console.error('Error deleting document:', err)
      return false
    }
  }

  // Escuchar cambios en tiempo real
  const subscribeToCollection = (constraints?: QueryConstraint[]) => {
    const collectionRef = collection(db, collectionName)
    const q = constraints ? query(collectionRef, ...constraints) : collectionRef
    
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const docs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as T[]
        
        setDocuments(docs)
        setLoading(false)
      },
      (err) => {
        const errorMessage = err instanceof Error ? err.message : 'Error en la suscripción'
        setError(errorMessage)
        console.error('Error in subscription:', err)
        setLoading(false)
      }
    )
    
    return unsubscribe
  }

  return {
    documents,
    loading,
    error,
    getDocuments,
    getDocument,
    addDocument,
    updateDocument,
    deleteDocument,
    subscribeToCollection,
    setError
  }
}

// Hook específico para blog posts
export interface BlogPost extends FirestoreDocument {
  title: string
  content: string
  excerpt: string
  author: string
  publishedAt: Timestamp | null
  status: 'draft' | 'published'
  tags: string[]
  imageUrl?: string
  slug: string
  metaDescription?: string
  readTime?: number
  type: 'article' | 'social_link' | 'video' // Nuevo: incluye tipo video
  videoUrl?: string // Nueva: URL del video de YouTube
  socialLinks?: {
    facebook?: string
    twitter?: string
    tiktok?: string
    instagram?: string
  }
}

export function useBlogPosts() {
  return useFirestore<BlogPost>('blog')
}

// Hook específico para casos de éxito
export interface SuccessCase extends FirestoreDocument {
  title: string
  description: string
  challenge: string
  solution: string
  results: string[]
  technologies: string[]
  industry: string
  clientName: string
  projectDate: string
  imageUrl?: string
  featured: boolean
}

export function useSuccessCases() {
  return useFirestore<SuccessCase>('success_cases')
}

// Hook específico para servicios
export interface Service extends FirestoreDocument {
  name: string
  description: string
  features: string[]
  benefits: string[]
  technologies: string[]
  price?: string
  duration?: string
  imageUrl?: string
  category: string
  active: boolean
}

export function useServices() {
  return useFirestore<Service>('services')
}

// Hook específico para miembros del equipo
export interface TeamMember extends FirestoreDocument {
  name: string
  position: string
  bio: string
  skills: string[]
  imageUrl?: string
  socialLinks: {
    linkedin?: string
    twitter?: string
    github?: string
    email?: string
  }
  order: number
  active: boolean
}

export function useTeamMembers() {
  return useFirestore<TeamMember>('team_members')
}

// Utilidades para fechas
export const formatFirestoreDate = (timestamp: Timestamp | null): string => {
  if (!timestamp) return ''
  return timestamp.toDate().toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

export const createSlug = (title: string): string => {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remover acentos
    .replace(/[^a-z0-9\s-]/g, '') // Remover caracteres especiales
    .replace(/\s+/g, '-') // Reemplazar espacios con guiones
    .replace(/-+/g, '-') // Remover guiones múltiples
    .trim()
}

// Utilidad para convertir URLs de YouTube a formato embed
export const getYouTubeEmbedUrl = (url: string): string => {
  if (!url) return ''
  
  // Patrones de URLs de YouTube
  const patterns = [
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)/
  ]
  
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match && match[1]) {
      return `https://www.youtube.com/embed/${match[1]}`
    }
  }
  
  return url // Si no coincide con ningún patrón, devolver la URL original
}

// Validar si una URL es de YouTube
export const isYouTubeUrl = (url: string): boolean => {
  if (!url) return false
  
  const patterns = [
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=[a-zA-Z0-9_-]+/,
    /(?:https?:\/\/)?(?:www\.)?youtu\.be\/[a-zA-Z0-9_-]+/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/[a-zA-Z0-9_-]+/
  ]
  
  return patterns.some(pattern => pattern.test(url))
}
